import { useEffect } from "react";
import { Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/auth-context";
import { useSubscription } from "@/hooks/use-subscription";
import { useToast } from "@/hooks/use-toast";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, isLoading, effectiveVenueId } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();

  const venueId = user?.venueId ?? null;
  const { data: subscription, isLoading: subLoading } = useSubscription(venueId);

  // Handle checkout URL params on mount
  useEffect(() => {
    const checkoutParam = searchParams.get("checkout");
    if (checkoutParam === "success") {
      toast({
        title: "Subscription activated!",
        description: "Your 7-day free trial has started.",
      });
      navigate(location.pathname, { replace: true });
    } else if (checkoutParam === "canceled") {
      toast({
        description: "You can complete payment anytime from Settings > Billing.",
      });
      navigate(location.pathname, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Subscription status computation (for customer routes only)
  const isAdminRoute = location.pathname.startsWith("/admin");
  const isOnSettings = location.pathname === "/app/settings";
  const subStatus = subscription?.status;
  const subIsActive = subStatus === "active" || subStatus === "trialing";
  const subIsInGrace =
    subStatus === "past_due" &&
    subscription != null &&
    subscription.gracePeriodEnd != null &&
    new Date(subscription.gracePeriodEnd) > new Date();

  // True when: customer route, sub loaded, sub exists, and not allowed
  const subscriptionExpired =
    !isAdminRoute &&
    user?.role !== "admin" &&
    !subLoading &&
    subscription !== undefined &&
    !subIsActive &&
    !subIsInGrace &&
    !isOnSettings;

  // Redirect + toast when subscription is expired
  useEffect(() => {
    if (subscriptionExpired) {
      toast({
        title: "Subscription expired",
        description: "Your subscription has expired. Please update your billing to continue.",
        variant: "destructive",
      });
      navigate("/app/settings?tab=billing", { replace: true });
    }
  }, [subscriptionExpired]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 rounded-full border-4 border-purple-500/20 border-t-purple-500 animate-spin" />
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Admin trying to access /app → redirect to /admin (unless impersonating a venue)
  if (user.role === "admin" && !effectiveVenueId && location.pathname.startsWith("/app")) {
    return <Navigate to="/admin" replace />;
  }
  // Customer trying to access /admin → redirect to /app
  if (user.role === "customer" && location.pathname.startsWith("/admin")) {
    return <Navigate to="/app" replace />;
  }

  // Only redirect to /app/welcome if customer has no venueId (admin always has null venueId — skip this check for admin)
  if (user.role !== "admin" && !user.venueId) {
    return <Navigate to="/app/welcome" replace />;
  }

  // Show loading while subscription status is being fetched for customer routes
  if (!isAdminRoute && user.role !== "admin" && !!venueId && subLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 rounded-full border-4 border-purple-500/20 border-t-purple-500 animate-spin" />
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Show loading while the expired-subscription redirect effect fires (prevents content flash)
  if (subscriptionExpired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 rounded-full border-4 border-purple-500/20 border-t-purple-500 animate-spin" />
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
